// This component uses the carousel from workouts but allows us to feed it our own data //

import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import CarouselCard from '../../../workouts/components/presentational/CarouselCard';
import Carousel from '../../../workouts/components/presentational/Carousel';
import MediaModalContainer from '../../../workouts/components/presentational/MediaModalContainer';
import { setIsPreviewCarouselShowing } from '../../ducks/navigatorActions';
import { setActiveWorkoutsMedia } from '../../../workouts/ducks/workoutsActions';

const CardWrapper = styled.div`
  pointer-events: none;
  width: 100%;
  height: 100%;
  position: relative;
`;

const VideoClickOverlay = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 30%;
  z-index: 5;
  cursor: pointer;
  pointer-events: auto;
  background: none;
  border: none;
  padding: 0;
`;

const PreviewCard = ({
  workoutData,
  isCarouselShowing,
  activeIndex,
  setActiveIndex,
  isFinalIndex,
}) => {
  const dispatch = useDispatch();
  const {
    item, index: outerIndex, sessionIndex, carouselIndex,
  } = workoutData;
  const showVideoOverlay = isCarouselShowing
    && item.exercise?.media?.length > 0
    && activeIndex === carouselIndex;

  return (
    <CardWrapper>
      {showVideoOverlay && (
        <VideoClickOverlay
          type='button'
          aria-label={`Play video for ${item.title}`}
          onClick={(e) => {
            e.stopPropagation();
            dispatch(setActiveWorkoutsMedia(item.exercise.media[0]));
          }}
        />
      )}
      <CarouselCard
        workoutObject={item}
        outerIndex={outerIndex}
        sessionIndex={sessionIndex}
        index={carouselIndex}
        activeIndex={activeIndex}
        setActiveIndex={setActiveIndex}
        isFinalIndex={isFinalIndex}
        isPreview
      />
    </CardWrapper>
  );
};

PreviewCard.propTypes = {
  workoutData: PropTypes.shape({
    item: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      exercise: PropTypes.shape({
        media: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
      }),
      tableHeaders: PropTypes.arrayOf(PropTypes.string),
      type: PropTypes.string,
      sessionBreakItems: PropTypes.instanceOf(Array),
    }).isRequired,
    index: PropTypes.number.isRequired,
    sessionIndex: PropTypes.number,
    carouselIndex: PropTypes.number.isRequired,
  }).isRequired,
  isCarouselShowing: PropTypes.bool.isRequired,
  activeIndex: PropTypes.number,
  setActiveIndex: PropTypes.func,
  isFinalIndex: PropTypes.bool,
};

PreviewCard.defaultProps = {
  activeIndex: undefined,
  setActiveIndex: undefined,
  isFinalIndex: false,
};

const PreviewCarousel = () => {
  const dispatch = useDispatch();

  const workoutItems = useSelector(
    (state) => state.navigator.data.currentWorkout.workoutItems || [],
  );
  const isWorkoutsCarouselShowing = useSelector(
    (state) => state.navigator.ui.isPreviewCarouselShowing,
  );

  const setPreviewCarouselShowing = (bool) => {
    dispatch(setIsPreviewCarouselShowing(bool));
    if (!bool) {
      dispatch(setActiveWorkoutsMedia({}));
    }
  };

  const workoutCardsMapper = (
    inputArray,
  ) => inputArray.map((workoutObject, index) => ({
    cardElement: (
      <PreviewCard
        workoutData={{ ...workoutObject, carouselIndex: index }}
        isCarouselShowing={isWorkoutsCarouselShowing}
      />
    ),
    shouldExpand: workoutObject.item.tableHeaders && workoutObject.item.tableHeaders.length >= 3,
    key: workoutObject.item.id,
  }));

  const flattenArray = (unflattenedArray) => {
    const flattenedArray = [];
    unflattenedArray.forEach((item, index1) => {
      flattenedArray.push({ item, index: index1 });
      if (item.type === 'BR') {
        item.sessionBreakItems.forEach((sessionBreakItem, innerIndex) => {
          flattenedArray.push({ item: sessionBreakItem, index: index1, sessionIndex: innerIndex });
        });
      }
    });
    return flattenedArray;
  };

  return (
    <>
      <Carousel
        cards={workoutCardsMapper(flattenArray(workoutItems))}
        isShowing={isWorkoutsCarouselShowing}
        setIsShowing={setPreviewCarouselShowing}
        hideExtraButtons
      />
      <MediaModalContainer />
    </>
  );
};

export default PreviewCarousel;
